import { useEffect, useState } from "react";

export type ThemePref = "dark" | "light" | "system";

const LS = "backstory-theme";

function systemTheme(): "dark" | "light" {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export function readThemePref(): ThemePref {
  try {
    const t = localStorage.getItem(LS);
    if (t === "light" || t === "dark" || t === "system") return t;
  } catch {
    /* ignore */
  }
  return "system";
}

export function resolvedTheme(pref: ThemePref): "dark" | "light" {
  return pref === "system" ? systemTheme() : pref;
}

export function applyTheme(pref: ThemePref): "dark" | "light" {
  const resolved = resolvedTheme(pref);
  const root = document.documentElement;
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
  try {
    localStorage.setItem(LS, pref);
  } catch {
    /* ignore */
  }
  return resolved;
}

const CYCLE: ThemePref[] = ["light", "dark", "system"];

function nextPref(p: ThemePref): ThemePref {
  return CYCLE[(CYCLE.indexOf(p) + 1) % CYCLE.length]!;
}

function icon(pref: ThemePref, resolved: "dark" | "light") {
  if (pref === "system") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 19h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (resolved === "dark") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M21 14.5A8.5 8.5 0 1 1 9.5 3 7 7 0 0 0 21 14.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 3v1.5M12 19.5V21M3 12h1.5M19.5 12H21M5.6 5.6l1.1 1.1M17.3 17.3l1.1 1.1M18.4 5.6l-1.1 1.1M6.7 17.3l-1.1 1.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export function ThemeToggle() {
  const [pref, setPref] = useState<ThemePref>(() => (typeof document === "undefined" ? "system" : readThemePref()));
  const resolved = resolvedTheme(pref);

  useEffect(() => {
    applyTheme(pref);
  }, [pref]);

  useEffect(() => {
    if (pref !== "system" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => applyTheme("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [pref]);

  const label = pref === "system" ? "System theme" : resolved === "dark" ? "Dark theme" : "Light theme";

  return (
    <button
      className="btn ghost icon-btn"
      onClick={() => setPref(nextPref(pref))}
      aria-label={`Theme: ${label}. Click to change.`}
      title={label}
    >
      {icon(pref, resolved)}
    </button>
  );
}
